/* eslint-disable jira/integration/test-id-by-folder-structure */

import React, { useCallback, useState } from 'react';
import Button from '@atlaskit/button/button';
import LoadingButton from '@atlaskit/button/loading-button';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog from '@atlaskit/modal-dialog/modal-dialog';
import type { KeyboardOrMouseEvent } from '@atlaskit/modal-dialog/types';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import ProgressBar from '@atlaskit/progress-bar/progress-bar';
import TextField from '@atlaskit/textfield/text-field';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useCollectionsActions } from '@jira/polaris__component-collections/src/useCollectionsActions.tsx';
import { useNavigationCollectionsActions } from '@jira/polaris__component-navigation-collections-store/src/useNavigationCollectionsActions.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition';
import { CopyToClipboard } from '@jira/polaris__lib-copy-to-clipboard/src/CopyToClipboard.tsx';
import { useNotifications } from '@jira/polaris__lib-notifications/src/useNotifications.tsx';
import {
	type UIAnalyticsEvent,
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import type { Props } from './UiProps.types.tsx';
import { ViewsList } from './ViewsList.tsx';

const useNavigationCollectionsActionsFG = functionWithCondition(
	() => fg('jpd-enable-roadmaps-recents-fe'),
	useNavigationCollectionsActions,
	// oxlint-disable-next-line typescript/consistent-type-assertions
	() => null as unknown as ReturnType<typeof useNavigationCollectionsActions>,
);

const header = defineMessage({
	id: 'polaris-component-collection-delete-modal.header',
	defaultMessage: 'Do you want to delete "{roadmapName}" roadmap?',
	description: 'Header of the Delete Roadmap confirmation modal window',
});

const cancel = defineMessage({
	id: 'polaris-component-collection-delete-modal.cancel',
	defaultMessage: 'Cancel',
	description:
		'Label for the cancel button in the collection delete confirmation modal, allowing the user to dismiss the dialog without deleting.',
});

const deleteMessage = defineMessage({
	id: 'polaris-component-collection-delete-modal.delete',
	defaultMessage: 'Delete',
	description:
		'Label for the confirm delete button in the collection delete confirmation modal that permanently removes the collection.',
});

const confirmationTitle = defineMessage({
	id: 'polaris-component-collection-delete-modal.confirmation-title',
	defaultMessage: 'Type "{roadmapName}" to confirm the deletion:',
	description: 'Title for Confirm roadmap deletion input',
});

const description = defineMessage({
	id: 'polaris-component-collection-delete-modal.description',
	defaultMessage:
		// eslint-disable-next-line @atlassian/i18n/enforce-plural-rules
		'This roadmap has <b>{viewsCount, plural, =1 {# view} other {# views}}</b>. If you delete it, <b>the views will be deleted.</b> It will not delete the ideas that were used in the roadmap.',
	description: 'Description for Delete Roadmap confirmation modal window',
});

const errorTitle = defineMessage({
	id: 'polaris-component-collection-delete-modal.error-title',
	defaultMessage: 'We couldn’t delete the roadmap',
	description: 'Flag title to communicate an error.',
});

const errorDescription = defineMessage({
	id: 'polaris-component-collection-delete-modal.error-description',
	defaultMessage: 'Please try again or refresh the page.',
	description: 'Flag description for an error occurring while deleting roadmap.',
});

export const CollectionDeleteModal = ({
	collectionUUID,
	onClose,
	onDelete,
	views,
	isLoading = false,
	collectionName,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { errorWithRefresh } = useNotifications();
	const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);
	const [confirmedCollectionName, setConfirmedCollectionName] = useState('');
	const { deleteCollection } = useCollectionsActions();
	const actions = useNavigationCollectionsActionsFG();
	const viewsCount = views.length;

	const handleCloseClick = useCallback(
		(event: KeyboardOrMouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'close');
			onClose(event);
		},
		[onClose],
	);

	const handelCancelClick = useCallback(
		(event: KeyboardOrMouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'cancel');
			onClose(event);
		},
		[onClose],
	);

	const handleDeleteClick = useCallback(
		async (event: KeyboardOrMouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'confirm');
			setIsDeleteInProgress(true);

			try {
				await deleteCollection(collectionUUID);
				if (actions && fg('jpd-enable-roadmaps-recents-fe')) {
					actions.runDeleteListeners?.();
					setIsDeleteInProgress(false);
				}
			} catch (error) {
				if (error instanceof Error) {
					errorWithRefresh({
						title: formatMessage(errorTitle),
						description: formatMessage(errorDescription),
					});
				}

				setIsDeleteInProgress(false);
				return;
			}
			onDelete?.(event);
		},
		[onDelete, deleteCollection, collectionUUID, actions, errorWithRefresh, formatMessage],
	);

	if (!collectionName) {
		return null;
	}

	return (
		<ModalTransition>
			<ContextualAnalyticsData sourceName="confirmDeleteCollection" sourceType={MODAL}>
				<ModalDialog
					testId="polaris-component-collection-delete-modal.ui.delete-confirmation-modal"
					onClose={handleCloseClick}
				>
					<FireScreenAnalytics />
					<ModalHeader hasCloseButton>
						<ModalTitle
							appearance="danger"
							testId="polaris-component-collection-delete-modal.ui.title"
						>
							{formatMessage(header, { roadmapName: collectionName })}
						</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<Stack space="space.200">
							{isLoading ? (
								<ProgressBar isIndeterminate />
							) : (
								<>
									{viewsCount > 0 && (
										<>
											<Text testId="polaris-component-collection-delete-modal.ui.description">
												{formatMessage(description, {
													viewsCount,
													b: (text: React.ReactNode) => <b>{text}</b>,
												})}
											</Text>
											<ViewsList collectionUUID={collectionUUID} views={views} />
										</>
									)}
								</>
							)}
							<Stack space="space.100">
								<Text>
									{formatMessage(confirmationTitle, {
										roadmapName: <CopyToClipboard text={collectionName} />,
									})}
								</Text>
								<TextField
									maxLength={255}
									value={confirmedCollectionName}
									onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
										setConfirmedCollectionName(event.target.value)
									}
									placeholder={collectionName}
									autoFocus
									isCompact
									testId="polaris-component-collection-delete-modal.ui.view-name-confirmation"
								/>
							</Stack>
						</Stack>
					</ModalBody>
					<ModalFooter>
						<Button onClick={handelCancelClick}>{formatMessage(cancel)}</Button>
						<LoadingButton
							isDisabled={isLoading || collectionName !== confirmedCollectionName}
							isLoading={isDeleteInProgress}
							onClick={handleDeleteClick}
							appearance="danger"
							testId="polaris-component-collection-delete-modal.ui.delete-button"
						>
							{formatMessage(deleteMessage)}
						</LoadingButton>
					</ModalFooter>
				</ModalDialog>
			</ContextualAnalyticsData>
		</ModalTransition>
	);
};
